import type { LucideIcon } from 'lucide-react';
import { MoveLeft, MoveRight } from 'lucide-react';

export type View = 'dashboard' | 'roadmap' | 'status-board' | 'cycle-detail';
export type SidePanelView = 'cycle-detail' | 'bet-preview' | 'roadmap-doc-preview' | null;
export type RoadmapViewMode = 'columns' | 'rows';
export type SectionColor = 'green' | 'blue' | 'yellow' | 'gray' | 'purple' | 'red' | 'orange';

export interface RoadmapItem {
  id: string;
  title: string;
  type?: string;
  appetite?: string;
  status?: string;
  priority?: number | string;
  progress?: number;
  value?: string;
  dependencies?: string[];
  category?: string;
  items?: string[];
  lastWorked?: string;
  nextSteps?: string;
  path?: string;
  boardOrder?: number;
  fileOrder?: number;
  mustHaveTotal?: number;
  mustHaveDone?: number;
  owner?: string;
  safeToDispatch?: boolean;
  needsShapingFirst?: boolean;
  dispatchTarget?: string;
  gitMrUrl?: string;
  workingBranch?: string;
  /** From bet frontmatter or roadmap YAML row */
  tags?: string[];
  /** Planning cycle number from bet frontmatter (e.g. 4) */
  cycle?: number;
  /** FlowNotes documents.id when bet file_path exists in DB (from roadmap enrich API). */
  documentId?: string;
  /** From bet frontmatter when a cloud agent was dispatched */
  dispatchedAgentId?: string;
  dispatchStatus?: string;
  /** Implementation handoff doc path (flownotes-app-docs/...) from bet body or frontmatter */
  handoffPath?: string;
  /** documents.id for handoff when indexed in DB */
  handoffDocumentId?: string;
}

export interface Section {
  id: string;
  title: string;
  color: SectionColor;
  badge?: string;
  sourceFilePath?: string;
  /** Query-backed column source (e.g. feedback-tickets) */
  source?: string;
  /** For feedback-tickets columns: frontmatter status filter / drag target status */
  statusFilter?: string | null;
  items: RoadmapItem[];
}

export const sectionColors: Record<SectionColor, { bg: string; text: string; border: string; hoverBg: string; dotBg: string }> = {
  green:  { bg: 'bg-green-500/10',  text: 'text-green-500',  border: 'border-green-500/20',  hoverBg: 'group-hover/column:bg-green-500/[0.06]',  dotBg: 'bg-green-500'  },
  blue:   { bg: 'bg-blue-500/10',   text: 'text-blue-500',   border: 'border-blue-500/20',   hoverBg: 'group-hover/column:bg-blue-500/[0.06]',   dotBg: 'bg-blue-500'   },
  yellow: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/20', hoverBg: 'group-hover/column:bg-yellow-500/[0.06]', dotBg: 'bg-yellow-500' },
  gray:   { bg: 'bg-gray-500/10',   text: 'text-gray-500',   border: 'border-gray-500/20',   hoverBg: 'group-hover/column:bg-gray-500/[0.04]',   dotBg: 'bg-gray-500'   },
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/20', hoverBg: 'group-hover/column:bg-purple-500/[0.06]', dotBg: 'bg-purple-500' },
  red:    { bg: 'bg-red-500/10',    text: 'text-red-500',    border: 'border-red-500/20',    hoverBg: 'group-hover/column:bg-red-500/[0.06]',    dotBg: 'bg-red-500'    },
  orange: { bg: 'bg-orange-500/10', text: 'text-orange-500', border: 'border-orange-500/20', hoverBg: 'group-hover/column:bg-orange-500/[0.06]', dotBg: 'bg-orange-500' },
};

export const ALL_SECTION_COLORS: SectionColor[] = ['green', 'blue', 'yellow', 'gray', 'purple', 'red', 'orange'];

export const KANBAN_COLUMN_ORDER = ['now', 'next', 'later', 'done'] as const;

export function moveToColumnMenuIcon(fromSectionId: string, toSectionId: string): LucideIcon {
  const a = KANBAN_COLUMN_ORDER.indexOf(fromSectionId as (typeof KANBAN_COLUMN_ORDER)[number]);
  const b = KANBAN_COLUMN_ORDER.indexOf(toSectionId as (typeof KANBAN_COLUMN_ORDER)[number]);
  if (a < 0 || b < 0) return MoveRight;
  if (b < a) return MoveLeft;
  return MoveRight;
}

export type StoredProductBoardUi = {
  v: 1;
  currentView?: View;
  sidePanelView?: SidePanelView;
  sidePanelBetId?: string | null;
  sidePanelRoadmapPath?: string | null;
  /** Roadmap tag/cycle filter chip row. Default collapsed. */
  filtersExpanded?: boolean;
};

export const PRODUCT_BOARD_UI_STORAGE_KEY = 'flownotes.productBoard.v1';
